import { Check } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/**
 * A scene's questions, rendered as tiles.
 *
 * Extracted so the start dialog and the inline panel ask them the same way.
 * They are asked at two different moments — once when setting a scene up, and
 * again whenever it is edited — and a second implementation would have drifted
 * within a week.
 *
 * Options are tiles rather than dropdowns because that is what the data is:
 * the step type is `tile_select`, and every option carries an emoji and a
 * one-line description. A <select> threw both away and made a six-question
 * setup read as six identical grey boxes labelled "Choose…".
 */

export interface SceneOptionItem { key: string; label: string; description?: string; emoji?: string }
export interface SceneField {
  key: string;
  label: string;
  options?: SceneOptionItem[];
  conditionalOptions?: Record<string, SceneOptionItem[]>;
  showWhen?: { field: string; values: string[] };
}
export interface SceneStep {
  id: string;
  title: string;
  type: string;
  fieldKey?: string;
  fields?: SceneField[];
  options?: SceneOptionItem[];
  placeholder?: string;
  showWhen?: { field: string; values: string[] };
}

export interface SceneEntry {
  key: string;
  label: string;
  field?: SceneField;
  step: SceneStep;
}

/** Flatten steps into individual fields, preserving order. */
export function flattenFields(steps: SceneStep[]): SceneEntry[] {
  const out: SceneEntry[] = [];
  for (const step of steps || []) {
    if (step.type === "textarea") {
      out.push({ key: step.fieldKey || "prompt", label: step.title, step });
    } else if (step.type === "tile_select") {
      out.push({
        key: step.fieldKey || step.id,
        label: step.title,
        field: { key: step.fieldKey || step.id, label: step.title, options: step.options },
        step,
      });
    } else if (step.fields) {
      for (const f of step.fields) out.push({ key: f.key, label: f.label, field: f, step });
    }
  }
  return out;
}

/** A field shows only when its own and its step's showWhen conditions hold. */
export function isVisible(entry: SceneEntry, answers: Record<string, string>): boolean {
  const conds = [entry.step.showWhen, entry.field?.showWhen].filter(Boolean) as { field: string; values: string[] }[];
  return conds.every((c) => c.values.includes(answers[c.field] || ""));
}

/** Options can depend on another answer via conditionalOptions. */
export function optionsFor(field: SceneField | undefined, answers: Record<string, string>): SceneOptionItem[] {
  if (!field) return [];
  if (field.conditionalOptions) {
    for (const [key, opts] of Object.entries(field.conditionalOptions)) {
      if (Object.values(answers).includes(key)) return opts;
    }
  }
  return field.options || [];
}

/** The questions a scene is currently asking, given the answers so far. */
export function visibleEntries(config: any, answers: Record<string, string>): SceneEntry[] {
  return flattenFields((config?.steps as SceneStep[]) || []).filter((e) => isVisible(e, answers));
}

export function SceneQuestions({
  entries, answers, onChange, columns = 3,
}: {
  entries: SceneEntry[];
  answers: Record<string, string>;
  onChange: (key: string, value: string) => void;
  /** The dialog is narrower than the panel, so it asks for two. */
  columns?: 2 | 3;
}) {
  return (
    <div className="space-y-5">
      {entries.map((entry) => {
        if (entry.step.type === "textarea") {
          return (
            <section key={entry.key} className="space-y-2">
              <SectionLabel>{entry.label}</SectionLabel>
              <Textarea
                value={answers[entry.key] || ""}
                onChange={(e) => onChange(entry.key, e.target.value)}
                placeholder={entry.step.placeholder || ""}
                rows={3}
                className="text-sm resize-none"
              />
            </section>
          );
        }

        const opts = optionsFor(entry.field, answers);
        return (
          <section key={entry.key} className="space-y-2">
            <SectionLabel>{entry.label}</SectionLabel>
            {/* A grid rather than wrapping fixed-width tiles: rows line up and
                every tile in a row is the same height regardless of how long
                its description runs. */}
            <div className={cn("grid gap-2", columns === 2 ? "grid-cols-2" : "grid-cols-2 lg:grid-cols-3")}>
              {opts.map((o) => {
                const isSelected = answers[entry.key] === o.key;
                return (
                  <button
                    key={o.key}
                    type="button"
                    // Clicking the chosen tile clears it. Without this an answer
                    // given by accident can never be undone — these fields have
                    // no "none" option.
                    onClick={() => onChange(entry.key, isSelected ? "" : o.key)}
                    aria-pressed={isSelected}
                    className={cn(
                      "group relative text-left rounded-xl border p-3 transition-all",
                      isSelected
                        ? "border-primary bg-primary/[0.06] shadow-sm"
                        : "border-border hover:border-primary/50 hover:bg-muted/40",
                    )}
                  >
                    {isSelected && (
                      <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                        <Check className="w-2.5 h-2.5" />
                      </span>
                    )}
                    {o.emoji && (
                      <span className="block text-xl leading-none mb-1.5" aria-hidden>{o.emoji}</span>
                    )}
                    <span className="block text-[13px] font-medium leading-snug pr-4">{o.label}</span>
                    {o.description && (
                      <span className="block text-[11px] text-muted-foreground leading-snug mt-0.5">
                        {o.description}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
      {children}
    </h3>
  );
}
